// ============================================================================
// PÁGINA: CLASSIFICAÇÃO (NÍVEIS DA FEDERAÇÃO) - VISUAL PREMIUM
// Arquivo: src/pages/publicacoes/Classificacao.tsx
// Descrição: Classificação por níveis (Prata, Ouro, Diamante) + regras e notas
// Ícones: public/icons (sem fundo)
// ============================================================================

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";

type Nivel = "Prata" | "Ouro" | "Diamante";

type NivelInfo = {
  key: Nivel;
  title: string;
  subtitle: string;
  icon: string;
  accent: string; // tailwind text/border/bg tokens
  requisitos: string[];
  reconhecimentos: string[];
};

const Classificacao: React.FC = () => {
  const niveis: NivelInfo[] = useMemo(
    () => [
      {
        key: "Prata",
        title: "Nível Prata",
        subtitle: "Profissional em Ascensão",
        icon: "/icons/prata.png",
        accent: "from-gray-200 to-gray-50",
        requisitos: [
          "Mínimo de 03 anos de atuação comprovada.",
          "Participação em formações contínuas e/ou congressos da área.",
          "Indicação ou reconhecimento por clientes ou pares.",
          "Ter atuado como assistente de cursos ou eventos.",
        ],
        reconhecimentos: [
          "Certificado oficial do nível.",
          "Formação de novos profissionais.",
        ],
      },
      {
        key: "Ouro",
        title: "Nível Ouro",
        subtitle: "Educador Reconhecido",
        icon: "/icons/ouro.png",
        accent: "from-amber-100 to-white",
        requisitos: [
          "Mínimo de 05 anos de atuação comprovada.",
          "Atuação como educador, palestrante, mentor ou multiplicador de conhecimento.",
          "Participação ativa em eventos oficiais da Federação.",
          "Contribuição com artigos, vídeos ou conteúdos para a comunidade.",
        ],
        reconhecimentos: [
          "Certificado oficial do nível.",
          "Perfil em destaque no site com mini biografia.",
          "Possibilidade de compor comissões internas.",
          "Formação de novos profissionais.",
        ],
      },
      {
        key: "Diamante",
        title: "Nível Diamante",
        subtitle: "Excelência Máxima",
        icon: "/icons/diamante.png",
        accent: "from-blue-100 to-white",
        requisitos: [
          "Mínimo de 07 anos de atuação comprovada.",
          "Reconhecimento nacional ou internacional.",
          "Participação em premiações, júris técnicos, ou publicações especializadas.",
          "Habilidades especiais reconhecidas por autoridade consular ou pela Federação.",
          "Formação de novos educadores ou multiplicadores.",
        ],
        reconhecimentos: [
          "Certificado oficial do nível.",
          "Condecoração de “Excelência Profissional”.",
          "Destaque permanente nas mídias da Federação.",
          "Participação em bancadas como jurado.",
          "Divulgação no site de seus artigos científicos.",
          "Convite preferencial para representar a Federação Nacional e Internacionalmente.",
          "Possibilidade de se tornar Embaixador Master.",
        ],
      },
    ],
    []
  );

  const [aberto, setAberto] = useState<Nivel>("Ouro");

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50/30 text-gray-900">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-amber-950 to-gray-950" />
        <div className="absolute inset-0 opacity-15">
          <div className="absolute -top-24 -left-24 w-[520px] h-[520px] bg-amber-500 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -right-24 w-[520px] h-[520px] bg-orange-500 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 relative z-10 py-16 md:py-20">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8">
              <img
                src="/icons/icon6.png"
                alt="Classificação"
                className="w-6 h-6 object-contain"
              />
              <span className="text-xs md:text-sm font-bold text-amber-100 uppercase tracking-wider">
                Classificação Oficial
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white">
              Classificação por Níveis
            </h1>

            <p className="mt-5 text-base md:text-lg text-white/85 max-w-4xl mx-auto leading-relaxed">
              Classificação por Níveis da Federação dos Profissionais da Beleza e Estética Brasil – Internacional.
              Reconhecemos e valorizamos a jornada e as conquistas dos nossos Filiados, do comprometimento inicial à
              excelência máxima, com critérios claros: tempo de atuação, capacitações, prêmios e contribuição na formação
              de outros profissionais.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/publicacoes/profissionais"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all"
              >
                <span>Consultar Profissionais</span>
                <ArrowRight className="w-5 h-5" />
              </Link>

              <Link
                to="/filiacao"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white/10 border border-white/20 backdrop-blur-md text-white font-bold rounded-xl hover:bg-white/15 transition-all"
              >
                <span>Tornar-se Filiado</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Chips */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-4xl mx-auto">
              {niveis.map((n) => (
                <button
                  key={n.key}
                  onClick={() => setAberto(n.key)}
                  className={`text-left rounded-2xl border transition-all px-5 py-4 ${
                    aberto === n.key
                      ? "bg-white/12 border-white/25 shadow-[0_10px_40px_-30px_rgba(0,0,0,.6)]"
                      : "bg-white/8 border-white/15 hover:bg-white/10"
                  }`}
                >
                  <div className="flex items-center gap-3 text-white">
                    <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center overflow-hidden">
                      <img src={n.icon} alt={n.title} className="w-7 h-7 object-contain" />
                    </div>
                    <div>
                      <div className="font-black">{n.title}</div>
                      <div className="text-sm text-white/75 font-semibold">{n.subtitle}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </section>

      {/* CONTEÚDO */}
      <section className="py-14 md:py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            {/* Cards dos níveis */}
            <div className="grid lg:grid-cols-3 gap-6">
              {niveis.map((n) => {
                const isOpen = aberto === n.key;

                return (
                  <div
                    key={n.key}
                    className={`bg-white rounded-3xl border border-amber-100/70 shadow-[0_20px_55px_-40px_rgba(0,0,0,.45)] overflow-hidden transition-all ${
                      isOpen ? "ring-2 ring-amber-200" : ""
                    }`}
                  >
                    {/* Top */}
                    <div className={`p-7 bg-gradient-to-br ${n.accent} border-b border-gray-100`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-2xl border border-gray-200 bg-white flex items-center justify-center overflow-hidden">
                            <img src={n.icon} alt={n.title} className="w-9 h-9 object-contain" />
                          </div>
                          <div>
                            <div className="text-xs font-black text-amber-700 uppercase tracking-wider">
                              Classificação • {n.key}
                            </div>
                            <div className="text-2xl font-black text-gray-900">{n.title}</div>
                            <div className="mt-1 text-sm font-semibold text-gray-700">{n.subtitle}</div>
                          </div>
                        </div>

                        <button
                          onClick={() => setAberto(isOpen ? ("Ouro" as Nivel) : n.key)}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 transition text-sm font-bold"
                          aria-label="Expandir"
                        >
                          Detalhes
                          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </button>
                      </div>
                    </div>

                    {/* Corpo */}
                    <div className="p-7">
                      <div className="text-sm font-black text-gray-900 mb-3">Requisitos</div>
                      <ul className="space-y-2 text-gray-700">
                        {n.requisitos.slice(0, isOpen ? n.requisitos.length : 3).map((r, i) => (
                          <li key={i} className="flex gap-3">
                            <span className="mt-2 w-2 h-2 rounded-full bg-amber-600 flex-shrink-0" />
                            <span className="leading-relaxed">{r}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-6 h-px w-full bg-gradient-to-r from-transparent via-amber-200/70 to-transparent" />

                      <div className="mt-6 text-sm font-black text-gray-900 mb-3">Reconhecimentos</div>
                      <ul className="space-y-2 text-gray-700">
                        {n.reconhecimentos.slice(0, isOpen ? n.reconhecimentos.length : 2).map((r, i) => (
                          <li key={i} className="flex gap-3">
                            <span className="mt-2 w-2 h-2 rounded-full bg-gray-900 flex-shrink-0" />
                            <span className="leading-relaxed">{r}</span>
                          </li>
                        ))}
                      </ul>

                      {!isOpen && (
                        <div className="mt-6 text-sm text-gray-600">
                          Clique em <span className="font-bold">Detalhes</span> para ver tudo.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* IMPORTANTE */}
            <div className="mt-10 rounded-3xl border border-gray-900/10 bg-gradient-to-br from-gray-900 to-gray-950 text-white p-7 md:p-10 overflow-hidden relative">
              <div className="absolute -top-20 -right-20 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-orange-500/20 rounded-full blur-3xl" />

              <div className="relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center overflow-hidden">
                    <img src="/icons/icon4.png" alt="Importante" className="w-7 h-7 object-contain" />
                  </div>
                  <div>
                    <div className="text-xl font-black">Importante</div>
                    <div className="text-white/75 text-sm font-semibold">
                      Regras gerais de promoção e validação dos selos
                    </div>
                  </div>
                </div>

                <ul className="mt-6 space-y-3 text-white/85">
                  <li className="flex gap-3">
                    <span className="mt-2 w-2 h-2 rounded-full bg-amber-300 flex-shrink-0" />
                    <span>
                      As promoções de nível são feitas <span className="font-black text-white">anualmente</span>, mediante envio de documentos comprobatórios e análise da{" "}
                      <span className="font-black text-white">
                        Comissão Nacional de Profissionalização e Boas Práticas da Federação
                      </span>.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 w-2 h-2 rounded-full bg-amber-300 flex-shrink-0" />
                    <span>
                      Cada selo é validado com <span className="font-black text-white">QR Code</span> para consulta pública.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 w-2 h-2 rounded-full bg-amber-300 flex-shrink-0" />
                    <span>
                      Os níveis também podem aparecer na <span className="font-black text-white">carteira digital ou física</span> dos Filiados.
                    </span>
                  </li>
                </ul>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/publicacoes/profissionais"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold hover:shadow-2xl hover:scale-[1.02] transition-all"
                  >
                    Consultar Profissionais <ArrowRight className="w-5 h-5" />
                  </Link>

                  <Link
                    to="/contato"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white font-bold hover:bg-white/15 transition-all"
                  >
                    Falar com a Federação <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>

            {/* FECHO */}
            <div className="mt-10 text-center text-gray-600">
              <p className="font-semibold">
                Cada nível reflete uma etapa de crescimento e reconhecimento profissional — com critérios claros e validação institucional.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Classificacao;
